import React, { useEffect, useMemo, useState } from "react";
import { Button, Input, Spin, Form, Select, message, Alert } from "antd";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../Common/CustomModal";
import SelectLinkedStudents from "./SelectLinkedStudents";
import { useGuardians } from "../hooks/useGuardians";
import { GuardianRelationship, GuardianFamilySituation } from "../../../../types/guardianTypes";
import { GUARDIAN_RELATIONSHIP_OPTIONS, GUARDIAN_FAMILY_SITUATION_OPTIONS } from "../../../../constants/guardianConstants";
import { isValidPhoneNumber } from "utils/validators";
import CommonSelectLite from "components/Common/CommonSelectLite";
import FamilySituationBadge from "components/Common/Badge/FamilySituationBadge";
import ImageWithoutBasePath from "components/Common/imageWithoutBasePath";

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess?: (guardian?: {
    email: string;
    firstName: string;
    lastName: string;
    phoneNumber?: string;
    relationship: GuardianRelationship;
    familySituation: GuardianFamilySituation;
    existingGuardianId?: string;
    studentIds?: string[]
  }) => void;
  studentId?: string;
  context: "student" | "guardian";
}

const AddGuardianModal: React.FC<Props> = ({ visible, onClose, onSuccess, studentId, context }) => {
  const { t } = useTranslation();
  const isStudentContext = context === "student";
  const [form] = Form.useForm();
  const [studentIds, setStudentIds] = useState<string[]>([]);
  const [relationship, setRelationship] = useState<GuardianRelationship>("father");
  const [familySituation, setFamilySituation] = useState<GuardianFamilySituation>("SINGLE");

  const [emailError, setEmailError] = useState(false);

  const {
    loading,
    matchedGuardian,
    notFound,
    search,
    resetSearch,
    linkStudents,
    fetchGuardians,
    createAndLinkGuardian,
  } = useGuardians();

  const emailValue = Form.useWatch("email", form);
  const finalStudentIds = useMemo(() => {
    if (isStudentContext && studentId) {
      const existingIds = matchedGuardian?.linkedStudents?.map(s => s.id) || [];
      return Array.from(new Set([...existingIds, studentId]));
    }
    return studentIds;
  }, [isStudentContext, studentId, studentIds, matchedGuardian]);

  const closeModal = () => {
    form.resetFields();
    resetSearch();
    setStudentIds([]);
    setRelationship("father");
    setFamilySituation("SINGLE")
    onClose();
  };

  const handleSearch = async () => {
    try {
      const { email } = await form.validateFields(["email"]);
      await search({ email });
    } catch { }
  };
  useEffect(() => {
    if (matchedGuardian?.linkedStudents?.length && !isStudentContext) {
      const ids = matchedGuardian.linkedStudents.map(s => s.id);
      setStudentIds(ids);
    }
  }, [matchedGuardian, isStudentContext]);

  const queueOrLinkGuardian = async (data: { email: string; firstName: string; lastName: string; phoneNumber?: string }) => {
    if (isStudentContext && !studentId) {
      if (!data.firstName || !data.lastName || !data.phoneNumber) {
        handleSearch()
        return
      }
      const existingIds = matchedGuardian?.linkedStudents?.map(s => s.id) || [];
      const combinedStudentIds = Array.from(new Set([...existingIds, ...studentIds]));

      onSuccess?.({
        ...data,
        relationship,
        familySituation,
        existingGuardianId: matchedGuardian?.id,
        studentIds: combinedStudentIds,
      });
      return closeModal();
    }

    if (!isStudentContext && finalStudentIds.length === 0) {
      return message.warning(t("please_select_students"));
    }

    try {
      if (matchedGuardian) {
        await linkStudents(matchedGuardian.id, finalStudentIds, relationship);
        message.success(t("students_linked_successfully"));
      } else {
        await createAndLinkGuardian(data, finalStudentIds, relationship, familySituation);
        message.success(t("guardian_created_and_linked"));
      }
      onSuccess?.();
      fetchGuardians();
      closeModal();
    } catch {
      message.error(t("failed_to_create_guardian"));
    }
  };
  const handleSave = async () => {
    try {
      const values = await form.validateFields(); // waits and throws if invalid

      if (matchedGuardian) {
        // If a matched guardian exists, use their info
        await queueOrLinkGuardian({
          email: matchedGuardian.user.email,
          firstName: matchedGuardian.user.firstName,
          lastName: matchedGuardian.user.lastName,
          phoneNumber: matchedGuardian.user.phoneNumber,
        });
      } else {
        // Otherwise, use the form values to create/link
        await queueOrLinkGuardian(values);
      }

    } catch (error) {
      // Validation failed — don't call the API
      console.warn("Form validation failed:", error);
    }
  };
  const renderMatchedGuardian = () => (
    <div className="mb-4">
      <div className="card border border-success mb-4">
        <div className="card-body d-flex align-items-center">
          <div className="me-3">
            <ImageWithoutBasePath
              src={matchedGuardian?.user?.photo ?? ""}
              alt={matchedGuardian?.user.firstName}
              className="rounded-circle"
              width={50}
              height={50}
            />
          </div>
          <div>
            <h5 className="mb-1 text-success">{t("account_found")}</h5>
            <div className="text-muted">
              <div><strong>{matchedGuardian?.user.firstName} {matchedGuardian?.user.lastName}</strong></div>
              <div>{matchedGuardian?.user.email}</div>
              <div>{matchedGuardian?.user.phoneNumber}</div>
              <div className="pt-2"><FamilySituationBadge situation={matchedGuardian?.familySituation || ""} size="sm" /></div>

            </div>
          </div>
        </div>
      </div>

      {!isStudentContext && (
        <>
          <label className="form-label">{t("students")}</label>
          <SelectLinkedStudents value={studentIds} initialStudents={matchedGuardian?.linkedStudents ?? []} onChange={setStudentIds} />
        </>
      )}

      <label className="form-label mt-3">{t("relationship")}</label>
      <CommonSelectLite
        value={relationship}
        onChange={setRelationship}
        options={GUARDIAN_RELATIONSHIP_OPTIONS(t)}
        optionValueKey="value"
        optionLabelKey="label"
      />
    </div>
  );

  const renderCreateForm = () => (
    <>
      <Form.Item name="firstName" label={t("first_name")} rules={[{ required: true, message: t("validation.firstName_required") }]}>
        <Input />
      </Form.Item>
      <Form.Item name="lastName" label={t("last_name")} rules={[{ required: true, message: t("validation.lastName_required") }]}>
        <Input />
      </Form.Item>
      <Form.Item name="phoneNumber" label={t("phone")} rules={[{ required: true, message: t("validation.phoneNumber_required") }, {
        validator: (_, value) =>
          value && !isValidPhoneNumber(value)
            ? Promise.reject(t("validation.phone_number_invalid"))
            : Promise.resolve(),
      }]}>
        <Input />
      </Form.Item>

      {!isStudentContext && (
        <Form.Item label={t("relationship")}>
          <SelectLinkedStudents value={studentIds} initialStudents={matchedGuardian?.linkedStudents ?? []} onChange={setStudentIds} />
        </Form.Item>
      )}

      <Form.Item label={t("relationship")}>
        <CommonSelectLite
          value={relationship}
          onChange={setRelationship}
          options={GUARDIAN_RELATIONSHIP_OPTIONS(t)}
          optionValueKey="value"
          optionLabelKey="label"
        />
      </Form.Item>

      <Form.Item label={t("guardianModule.familySituation")}>
        <CommonSelectLite
          value={familySituation}
          onChange={setFamilySituation}
          options={GUARDIAN_FAMILY_SITUATION_OPTIONS(t)}
          optionValueKey="value"
          optionLabelKey="label"
        />
      </Form.Item>

    </>
  );

  return (
    <CustomModal isOpen={visible} onClose={closeModal} onSave={handleSave} title={t("add_new_guardian")} width="600px">
      <Spin spinning={loading}>
        <Form
          form={form}
          layout="vertical"
          onFieldsChange={() => {
            const error = form.getFieldError("email");
            setEmailError(error.length > 0);
          }}
        >
          <Form.Item
            name="email"
            label={t("email")}
            rules={[
              { required: true, message: t("validation.please_enter_email") },
              { type: "email", message: t("validation.please_enter_valid_email") },
            ]}
          >
            <div style={{ display: "flex", gap: "8px" }}>
              <Input placeholder={t("enter_email")} />
              <button className="btn btn-primary" onClick={handleSearch} disabled={!emailValue || emailError}>
                {t("search")}
              </button>
            </div>
          </Form.Item>

          {matchedGuardian && renderMatchedGuardian()}
          {notFound && (
            <>
              <Alert type="info" showIcon className="mb-4" message={t("no_guardian_found")} description={t("you_can_create_new_guardian_with_email")} />
              {renderCreateForm()}
            </>
          )}
        </Form>
      </Spin>
    </CustomModal>
  );
};


export default AddGuardianModal;
